import React, { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

import { usePageSellerId } from '@dashboard/hooks/usePage';
import useProductQueries from '@dashboard/hooks/queries/useProductQueries';
import useSearchInput from '@/dashboard/hooks/useSearchInput';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { sellerProductsKey } from '@dashboard/lib/queryKeys';
import { cn } from '@dashboard/lib/utils';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { Command, CommandEmpty, CommandItem, CommandList } from '@dashboard/components/ui/command';
import { Popover, PopoverAnchor, PopoverContent } from '@dashboard/components/ui/popover';
import { Input } from '@dashboard/components/ui/input';
import { Skeleton } from '@dashboard/components/ui/skeleton';

// Plain controlled component (not a Formik field type) — `value`/`onChange` carry
// the full product, not an id, so there's no separate rehydration fetch: the input
// just mirrors `value.title`.
//
// Typing must NEVER call the external `onChange` — callers (e.g. the order-bumps
// attach flow) wire it straight to a commit/detach mutation, so firing it mid-type
// would read as "detach". `isEditing` tracks that divergence purely locally: it
// blocks the value->searchInput rehydration effect while the user is typing, and
// reverts the input back to `value.title` if they close the popover without
// picking anything. Only handleSelect/handleClear ever call the real `onChange`.
const ProductSelect = ({
  value = null,
  onChange,
  // Products already placed. They stay in the list, greyed and unselectable —
  // hiding them made a page whose bumps were all placed look like it had none.
  placedIds = [],
  // Optional right-hand line per result. The picker knows nothing about what it
  // is listing, so the caller supplies whatever is worth saying about a row.
  renderItemMeta,
  // Returns why a product cannot be picked here, or nothing. Greyed like a
  // placed product, with the reason where "Added" would sit.
  getUnavailableReason,
  placeholder = 'Search for a product',
  emptyMessage = 'No products found',
  // Server-side filter, so paging and `has_more` stay correct — filtering the
  // fetched page client-side would show short pages and a wrong end-of-list.
  role,
}) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { searchInput, setSearchInput, search } = useSearchInput(value?.title ?? '');

  const { getProducts } = useProductQueries();
  const sellerId = usePageSellerId();

  useEffect(() => {
    setIsEditing(false);
    setSearchInput(value?.title ?? '');
  }, [value?._id, value?.title, setSearchInput]);

  const showingSelectedValue = !!value && !isEditing;
  const effectiveSearch = showingSelectedValue || searchInput === '' ? '' : search;

  const { data, fetchNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: [...sellerProductsKey(sellerId, effectiveSearch), role ?? ''],
    queryFn: async ({ pageParam }) =>
      await getProducts(sellerId, {
        limit: 10,
        skip: pageParam * 10,
        search: effectiveSearch === '' ? undefined : effectiveSearch,
        ...(role ? { role } : {}),
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;
      return allPages.length;
    },
  });

  const placedIdSet = new Set(placedIds);
  const pages = data?.pages ?? [];

  const hasItems = pages?.[0]?.data?.length > 0;

  const handleSelect = product => {
    setIsEditing(false);
    setSearchInput(product.title);
    setOpen(false);
    onChange(product);
  };

  const handleClear = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditing(false);
    setSearchInput('');
    onChange(null);
    inputRef.current?.focus();
  };

  const handleOpenChange = nextOpen => {
    setOpen(nextOpen);
    if (!nextOpen) {
      // Abandoned without picking anything — revert the input to the committed
      // value instead of leaving the user's unsubmitted search text displayed.
      setIsEditing(false);
      setSearchInput(value?.title ?? '');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            value={searchInput ?? ''}
            placeholder={placeholder}
            autoComplete="off"
            aria-label={placeholder}
            onChange={e => {
              setIsEditing(true);
              setSearchInput(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="pr-16"
          />
          <div className="absolute inset-y-0 right-2 flex items-center gap-1 pointer-events-none">
            {searchInput?.length > 0 && (
              <button
                type="button"
                onMouseDown={handleClear}
                className="text-muted-foreground transition-colors hover:text-foreground pointer-events-auto"
                aria-label="Clear selection"
              >
                <XMarkIcon className="size-4" />
              </button>
            )}
            {showingSelectedValue ? (
              <ChevronDownIcon className="size-4 text-muted-foreground" />
            ) : (
              <MagnifyingGlassIcon className="size-4 text-muted-foreground/60" />
            )}
          </div>
        </div>
      </PopoverAnchor>

      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
        onInteractOutside={e => {
          if (inputRef.current && inputRef.current.contains(e.target)) {
            e.preventDefault();
          }
        }}
      >
        <Command shouldFilter={false} value={value?._id}>
          <CommandList className="max-h-72 p-1 scrollbar-hide">
            {isError && <CommandEmpty>Error loading products</CommandEmpty>}
            {!isError && !isLoading && !hasItems && <CommandEmpty>{emptyMessage}</CommandEmpty>}

            <BottomScrollListener onBottom={fetchNextPage} isLoadingMore={isLoading}>
              {pages.map(group =>
                group?.data?.map(product => {
                  const isPlaced = placedIdSet.has(product._id);
                  const unavailableReason = isPlaced ? null : getUnavailableReason?.(product);
                  const isDisabled = isPlaced || !!unavailableReason;

                  return (
                    // A faint rule separates results; the last of a page drops it
                    // so the list does not end on a line.
                    <div key={product._id} className="border-b border-border/60 last:border-b-0">
                      <CommandItem
                        value={product._id}
                        disabled={isDisabled}
                        onSelect={() => handleSelect(product)}
                        className={cn(
                          'flex min-w-0 items-center gap-2 rounded-none',
                          isDisabled && 'opacity-50',
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold">{product.title}</div>
                          {product.shortDescription && (
                            <div className="truncate text-sm text-muted-foreground">
                              {product.shortDescription}
                            </div>
                          )}
                          <div className="text-sm text-muted-foreground">
                            {getProductPriceLabel(product)}
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-xs text-muted-foreground">
                          {renderItemMeta?.(product)}
                          {isPlaced && <div className="text-foreground">Added</div>}
                          {unavailableReason && (
                            <div className="text-foreground">{unavailableReason}</div>
                          )}
                        </div>
                      </CommandItem>
                    </div>
                  );
                }),
              )}
            </BottomScrollListener>

            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  className="flex items-center gap-2 rounded-md px-1.5 py-1"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex h-4 items-center">
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <div className="flex h-5 items-center">
                      <Skeleton className="h-3.5 w-40" />
                    </div>
                  </div>
                  <Skeleton className="size-4 shrink-0 rounded-md" />
                </div>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ProductSelect;
